import { evictionReasonFieldFromOptions } from '@ee/common/forms';
import { CustomFieldType, IntakeFormSections } from '@ee/common/enums';
import {
  CustomField,
  IntakeForm,
  IntakeFormSection,
  LAYOUT_COLUMNS,
  LAYOUT_VERSION_LEGACY,
  LAYOUT_VERSION_ROWS,
  LayoutItem,
  LayoutRow,
  legacyColumnsForSpan,
  migrateFieldsToRows,
  IntakeOption
} from '@ee/common/models';
import {
  FieldDefinitionView,
  LayoutPageView,
  LayoutSectionView,
  isIntakePlaceable,
  lockReasonFor,
  sizeHintForColumns
} from './intake-layout.model';
import { COMPOSITE_PART_KEYS } from './composites';
import { dedupeByKey, repackOverfullRows } from './layout-row-pack';
import {
  DEFAULT_PAGES,
  isMergedFormSection,
  isPublicFormSection,
  NOTICE_DATE_KEY,
  PAST_DUE_RENT_KEY,
  PUBLIC_FORM_SECTIONS,
  publicFormSection,
  RENT_HISTORY_KEY,
  WAIVER_OF_NOTICE_KEY
} from './public-form.layout';

export {
  isMergedFormSection,
  isPublicFormSection,
  NOTICE_DATE_KEY,
  PAST_DUE_RENT_KEY,
  RENT_HISTORY_KEY,
  WAIVER_OF_NOTICE_KEY
};

/**
 * Between the stored `IntakeForm` and what the builder edits.
 *
 * The adapter is where the mixed-version estate is absorbed: a v1 form is projected into rows so it can
 * be *shown* in the v2 canvas, but nothing is written back until the firm is actually migrated. Reading
 * and writing are separate functions for exactly that reason — `toPages` never mutates.
 */

/** Keys v1 rendered to a client even when the option said `hide_from_intake` — the class only shaded them. */
const SHADED_NOT_HIDDEN = new Set(['past_due_rent', 'notice_date']);

/**
 * A form's keys that only the firm is asked — v1's `hide_from_intake` on a built-in option.
 *
 * v1 kept the control and skipped the markup for a client (`showField`), so the attorney filling the
 * same form saw the question (`overrideFieldVisibility`) and the client did not.
 */
export function attorneyOnlyKeys(options: Record<string, IntakeOption> | null | undefined): Set<string> {
  return new Set(
    Object.keys(options ?? {}).filter(key => options?.[key]?.hide_from_intake === true && !SHADED_NOT_HIDDEN.has(key))
  );
}

/**
 * A firm's own fields it marked `hide_from_intake`: asked of the firm, never of a client (v1's
 * `[hidden]="f.hide_from_intake && !overrideFieldVisibility"` on every custom field in Claims and Lease
 * Terms). A never-migrated form drops them from its rows; a migrated one may still carry them.
 */
export function attorneyOnlyFieldKeys(form: IntakeForm): Set<string> {
  return new Set(
    sectionsOf(form)
      .flatMap(section => [...(section.built_in_fields ?? []), ...(section.fields ?? [])])
      .filter(field => field.hide_from_intake === true && !!field.field)
      .map(field => field.field)
  );
}

/**
 * The pages a *client* is asked, with the firm-only questions removed: the built-in options that say
 * `hide_from_intake`, and the firm's own fields that do (`attorneyOnlyFieldKeys`).
 *
 * Applied by the host, not at projection: the builder canvas and the attorney intake keep the question,
 * and a control that is not placed is neither validated nor submitted — the same null v1 filed.
 */
export function withoutAttorneyOnly(
  pages: LayoutPageView[],
  options: Record<string, IntakeOption> | null | undefined,
  fieldKeys: Iterable<string> = []
): LayoutPageView[] {
  const keys = new Set([...attorneyOnlyKeys(options), ...fieldKeys]);
  if (!keys.size) {
    return pages;
  }
  return pages
    .map(page => ({
      ...page,
      sections: page.sections
        .map(section => ({
          ...section,
          rows: section.rows
            .map(row => ({ ...row, items: row.items.filter(item => !item.field_key || !keys.has(item.field_key)) }))
            .filter(row => row.items.length)
        }))
        // A section emptied by this is not a card to show.
        .filter((section, index) => section.rows.length || !page.sections[index].rows.length)
    }))
    // Nor is a page it leaves with no sections a step — the wizard reads a sectionless page as the
    // generated Review step, so leaving it would add a second one. Review itself never had sections.
    .filter((page, index) => page.sections.length || !pages[index].sections.length);
}


/** The note the documents step collects, rendered by the wizard rather than stored as a field. */
export const DOCUMENTS_NOTE_FIELD = 'note';

/** Its own section, so it reads as a separate ask rather than as one more upload row. */
export const NOTES_SECTION_KEY = 'NOTES';

/**
 * The Notes section of the documents page.
 *
 * The wizard renders a "Notes for the attorney" panel right after the uploads — hard-coded markup, and
 * its own panel, not a row inside the documents list. So it is its own section here too: a free-text
 * note to the firm is a different kind of ask from a file to upload, and folding it into the upload
 * list made it read as one more document.
 *
 * Fixed throughout: the section cannot be renamed or removed and the placement cannot be moved, because
 * it is part of what the step is for rather than something a firm arranged.
 */
function notesSection(): LayoutSectionView {
  return {
    key: NOTES_SECTION_KEY,
    title: 'Notes',
    rows: [
      {
        id: 'documents-note-row',
        items: [
          {
            kind: 'FIELD',
            field_key: DOCUMENTS_NOTE_FIELD,
            span: LAYOUT_COLUMNS,
            label_override: null,
            required_intake: false,
            lock_reason: 'PRODUCT_OWNED'
          }
        ]
      }
    ],
    firm_only: false,
    content_owned_by_product: true,
    full_width_only: true,
    hide_field_keys: true,
    locked_reason: 'A note to your firm, asked at the end of the upload step. Its wording is ours.'
  };
}

/** Sections whose contents staff own and the client never sees. */
const FIRM_ONLY_SECTIONS: IntakeFormSections[] = [IntakeFormSections.INTERNAL];

export function sectionKeyOf(key: string, section: IntakeFormSection): string {
  return section.key || key;
}

/**
 * Project a form onto pages the builder can edit.
 *
 * A v1 section is *shown* as rows — computed with the same greedy pack the server uses — without being
 * written back. That is what lets a firm on v1 open the new canvas and see their form laid out correctly
 * before anyone commits them to a migration.
 */
export function toPages(form: IntakeForm): LayoutPageView[] {
  const sections = sectionMap(form);

  if (form.pages?.length) {
    return form.pages.map(page => ({
      id: page.id,
      name: page.name,
      public_only: page.public_only,
      fixed: page.fixed,
      post_submit: page.post_submit,
      sections: page.section_keys.map(key => sections.get(key)).filter((s): s is LayoutSectionView => !!s)
    }));
  }

  const pages: LayoutPageView[] = DEFAULT_PAGES.map(page => ({
    id: page.id,
    name: page.name,
    public_only: page.publicOnly,
    fixed: page.fixed,
    sections: page.sections.map(key => sections.get(key)).filter((s): s is LayoutSectionView => !!s)
  })).filter(page => page.sections.length);

  // The documents page carries the Notes section alongside the uploads.
  pages
    .filter(page => page.id === 'page-documents' && !page.sections.some(s => s.key === NOTES_SECTION_KEY))
    .forEach(page => page.sections.push(sections.get(NOTES_SECTION_KEY)));

  // Review is fixed and always last; it is generated rather than authored, so it holds no sections.
  pages.push({
    id: 'page-review',
    name: 'Review & file',
    fixed: true,
    note:
      'Everything entered so far is summarised here for the client to check before filing. It is built ' +
      'from the pages above, so there is nothing to lay out — change a page and this follows.',
    sections: []
  });
  return pages;
}

function sectionMap(form: IntakeForm): Map<string, LayoutSectionView> {
  const map = new Map<string, LayoutSectionView>();
  const raw = form.sections as unknown as Record<string, IntakeFormSection>;

  Object.keys(raw ?? {}).forEach(key => {
    const section = raw[key];
    if (!section) return;
    map.set(sectionKeyOf(key, section), toSectionView(key, section));
  });

  // Page one is declared, not read: its questions live in two components rather than in the data, so
  // whatever the section map holds for them is not what the form asks.
  // The form's options, read once and translated into presence — see `publicFormSection`.
  const options = (form.options ?? {}) as unknown as Record<string, IntakeOption>;
  PUBLIC_FORM_SECTIONS.forEach((declared, index) =>
    map.set(declared.key, publicFormSection(raw?.[declared.key], index, options))
  );

  // Same for Notes. It is in the map rather than only appended to the default page so that a form whose
  // pages have been saved resolves it too — otherwise the section disappeared the moment a firm's own
  // page list existed, which is every firm after their first edit.
  map.set(NOTES_SECTION_KEY, notesSection());

  (form.custom_sections ?? []).forEach(section => {
    const key = section.key ?? section.title;
    if (key) map.set(key, toSectionView(key, section));
  });

  return map;
}


/**
 * The Documents section, where a repeated storage key is not a duplicate.
 *
 * Everywhere else on the form, one key means one question — placing a field twice would give two
 * editors one storage key. Documents are the exception by design: `field` on an upload is the
 * *attachment type*, and a form routinely asks for several of the same type. Nine of the twenty-nine
 * forms in a sample estate do; one asks for eight uploads across four types, three of them labelled
 * "Other Document". So a document slot's name is the whole of its identity to the client, and it lives
 * on the entry rather than on the type.
 *
 * That is what made three different uploads read as "Lease Agreement" three times over: the tile fell
 * back to the one definition the type has, and Lease Addenda and HAP Contract had nowhere to put their
 * own names.
 *
 * A form saved before this existed has rows carrying no label at all, so the names are matched back on
 * read — in order, per type, from the entries in `fields`. Within a type that is a best-effort
 * reconstruction: the rows have been dragged around since, and nothing in them records which entry
 * each came from. It is right for the common case, it is right about *which* names the section has,
 * and the first save pins whatever the firm sees.
 */
/**
 * An upload as Documents places it: half width, at whatever column its turn in the pair gives it. An
 * authored `col_start` is dropped — the section owns the placement now, and a stored start of 7 on the
 * first of a pair would push its partner past the 12th column.
 */
function asPairedUpload(item: LayoutItem, half: number): LayoutItem {
  const placed: LayoutItem = { ...item, span: half };
  delete placed.col_start;
  return placed;
}

/**
 * Documents in v1's two-column grid: every upload at half width, two to a row, in the order the rows
 * hold them. Packed when the section is read, so a form saved with one upload per row (the earlier
 * full-width layout) is drawn the same way without being re-saved. Anything else placed here (a
 * heading, or a non-upload field) keeps a row of its own at its own width.
 *
 * Row ids are positional (`document-row-N`, the same scheme `documentRows` starts from). The view is
 * written back on save, so an id derived from the stored one would grow on every save and reload.
 */
export function pairDocumentRows(rows: LayoutRow[], isUpload: (key: string) => boolean): LayoutRow[] {
  const half = LAYOUT_COLUMNS / 2;
  const out: LayoutRow[] = [];
  const nextId = () => `document-row-${out.length}`;
  let open: LayoutRow | null = null;
  for (const row of rows) {
    for (const item of row.items) {
      if (!item.field_key || !isUpload(item.field_key)) {
        open = null;
        out.push({ id: nextId(), items: [item] });
      } else if (open && open.items.length < 2) {
        open.items.push(asPairedUpload(item, half));
      } else {
        open = { id: nextId(), items: [asPairedUpload(item, half)] };
        out.push(open);
      }
    }
  }
  return out;
}

function documentRows(section: IntakeFormSection, stored: LayoutRow[] | null): LayoutRow[] {
  /*
   * Deduped across the two arrays, never within one.
   *
   * The same slot listed in both `built_in_fields` and `fields` is one upload written down twice —
   * that is what made Documents repeat itself. Two entries of one type inside `fields` are two
   * different uploads. Type alone cannot tell those apart, so the pair does: same type *and* same name
   * in both arrays is the duplicate, and the firm's own list wins.
   */
  const builtIn = section.built_in_fields ?? [];
  const own = section.fields ?? [];
  const pair = (field: CustomField) => `${field.field}\u0000${field.label ?? ''}`;
  const ownPairs = new Set(own.map(pair));
  const slots = [...builtIn.filter(field => !ownPairs.has(pair(field))), ...own];

  if (!stored) {
    // One row per slot: an upload is full width, so there is never more than one to a row.
    return slots.map((slot, index) => ({
      id: `document-row-${index}`,
      items: [
        {
          kind: 'FIELD' as const,
          field_key: slot.field,
          span: LAYOUT_COLUMNS,
          label_override: slot.label ?? null,
          hint: slot.hint ?? null,
          required_intake: slot.required ?? false
        }
      ]
    }));
  }

  // One queue per attachment type, drained in the order the rows appear.
  const unclaimed = new Map<string, CustomField[]>();
  slots.forEach(slot => {
    const queue = unclaimed.get(slot.field) ?? [];
    queue.push(slot);
    unclaimed.set(slot.field, queue);
  });

  return stored.map(row => ({
    ...row,
    items: row.items.map(item => {
      if (!item.field_key || item.label_override) {
        return item;
      }
      const slot = unclaimed.get(item.field_key)?.shift();
      return slot ? { ...item, label_override: slot.label ?? null, hint: item.hint ?? slot.hint ?? null } : item;
    })
  }));
}

export function toSectionView(key: string, section: IntakeFormSection): LayoutSectionView {
  const documents = sectionKeyOf(key, section) === String(IntakeFormSections.DOCUMENTS);
  // The section's own slots are its uploads; only those are paired into v1's two-column grid.
  const uploadKeys = new Set(
    [...(section.built_in_fields ?? []), ...(section.fields ?? [])]
      .filter(field => field.type === CustomFieldType.FILE_UPLOAD)
      .map(field => field.field)
  );
  const isUploadSlot = (fieldKey: string) => uploadKeys.has(fieldKey);

  const stored = section.layout_version === LAYOUT_VERSION_ROWS && section.rows?.length ? section.rows : null;

  /*
   * A v1 section is shown as rows but not written back. `migrateFieldsToRows` is the same greedy pack
   * the server runs, so what the canvas shows is what the migration would produce.
   *
   * Deduped by key. A section that holds the same field in both `built_in_fields` and `fields` — or
   * twice in one of them — otherwise projects two independently-movable tiles for one placement.
   * Documents are the exception and have their own projection: a repeated key there is a second
   * upload, not a duplicate.
   */
  const legacyRows = () =>
    migrateFieldsToRows(dedupeByKey([...(section.built_in_fields ?? []), ...(section.fields ?? [])]));

  const rows = documents ? documentRows(section, stored) : stored ? repackOverfullRows(stored) : legacyRows();

  /*
   * The expanded hint an ordinary section carries.
   *
   * Claims and Lease Terms are stored sections, so their built-in questions never went through the
   * declaration — but v1 still renders "Hover for more info" beside them from
   * `localization['<key>_hint']`, and v2 read neither store. The placement's own hint wins where one
   * has been authored since.
   */
  const localized = (item: LayoutRow['items'][number]) =>
    item.field_key ? (section.localization?.[`${item.field_key}_hint`] ?? null) : null;

  return {
    key: sectionKeyOf(key, section),
    title: section.title,
    description: section.description,
    /*
     * Upload boxes are laid out the way v1 draws them: labelled cards, two to a row. Their width is
     * fixed rather than resizable, so the resize handle is not offered.
     */
    full_width_only: documents,
    // `LEASE_AGREEMENT` and friends are AttachmentType names, not something a firm named or can use.
    hide_field_keys: documents,
    // A row that held nothing but an unplaceable field disappears with it, rather than rendering empty.
    rows: (documents ? (r: LayoutRow[]) => pairDocumentRows(r, isUploadSlot) : (r: LayoutRow[]) => r)(
      rows
        .map(row => ({
          id: row.id,
          items: row.items
            .filter(item => !item.field_key || isIntakePlaceable(item.field_key))
            .map(item => ({
              ...item,
              hint: item.hint ?? localized(item),
              lock_reason: item.lock_reason ?? lockReasonFor(item.field_key)
            }))
        }))
        .filter(row => row.items.length)
    ),
    firm_only: section.firm_only ?? FIRM_ONLY_SECTIONS.includes(key as IntakeFormSections)
  };
}

/**
 * Write the edited pages back onto the form.
 *
 * Only rows, pages and the layout version are touched. Definitions, bindings, required flags, rules and
 * merge fields are left exactly as they were, which is the same promise the server-side migrator makes —
 * and the reason a v1 firm can be shown this canvas without being migrated by accident.
 */
export function applyPages(form: IntakeForm, pages: LayoutPageView[]): IntakeForm {
  const raw = form.sections as unknown as Record<string, IntakeFormSection>;
  const byKey = new Map<string, IntakeFormSection>();
  Object.keys(raw ?? {}).forEach(key => byKey.set(sectionKeyOf(key, raw[key]), raw[key]));
  (form.custom_sections ?? []).forEach(section => {
    const key = section.key ?? section.title;
    if (key) byKey.set(key, section);
  });

  pages.forEach(page =>
    page.sections.forEach(view => {
      // The arrangement of page one is the firm's and is saved; the questions on it are the product's
      // and are not. A field is stored as its key alone, so a heading keeps the position it was dragged
      // to, while no copy of a span, label or requiredness the components own is persisted to go stale.
      // Synthesised, with no stored section behind it: the wizard owns both the question and its
      // wording, and there is nothing here a firm can have changed.
      if (view.key === NOTES_SECTION_KEY) {
        return;
      }
      // Declared, but not wholly the product's — written back whole, so the firm's own property fields
      // survive the save. What the declaration owns is re-derived on read regardless.
      if (isPublicFormSection(view.key) && !isMergedFormSection(view.key)) {
        const owned = byKey.get(view.key);
        if (owned) {
          // The arrangement is the firm's; the question is the wizard's. So a field is stored as its key
          // plus what a firm can change about it — where it sits, how wide, and whether it is required —
          // and never a copy of the label or type, which would go stale the moment the wizard changed.
          owned.rows = view.rows.map(row => ({
            id: row.id,
            items: row.items.map(item =>
              item.field_key
                ? {
                  kind: item.kind,
                  field_key: item.field_key,
                  span: item.span,
                  required_intake: item.required_intake
                }
                : item
            )
          }));
        }
        return;
      }
      const section = byKey.get(view.key);
      if (!section) return;
      section.title = view.title;
      section.rows = view.rows;
      section.layout_version = LAYOUT_VERSION_ROWS;
      section.firm_only = view.firm_only;
      // The derived width, kept for one release so anything still reading v1 keeps working. Rows and
      // spans are the source of truth; this is never authored.
      view.rows.forEach(row => row.items.forEach(item => (item.derived_columns = legacyColumnsForSpan(item.span))));
    })
  );

  form.pages = pages.map(page => ({
    id: page.id,
    name: page.name,
    public_only: page.public_only,
    fixed: page.fixed,
    post_submit: page.post_submit,
    section_keys: page.sections.map(section => section.key)
  }));
  form.layout_version = LAYOUT_VERSION_ROWS;
  return form;
}

/** True while any section is still on the legacy layout — the firm has not been migrated yet. */
export function isLegacyForm(form: IntakeForm): boolean {
  return (form.layout_version ?? LAYOUT_VERSION_LEGACY) < LAYOUT_VERSION_ROWS;
}

function sectionsOf(form: IntakeForm): IntakeFormSection[] {
  const raw = form.sections as unknown as Record<string, IntakeFormSection> | undefined;
  return [...Object.values(raw ?? {}), ...(form.custom_sections ?? [])].filter(section => !!section);
}

/** Field keys sitting on a stored row. Content blocks have none. */
function placedFieldKeys(form: IntakeForm): Set<string> {
  const keys = new Set<string>();
  sectionsOf(form).forEach(section =>
    section.rows?.forEach(row =>
      row.items?.forEach(item => {
        if (item.kind === 'FIELD' && item.field_key) {
          keys.add(item.field_key);
        }
      })
    )
  );
  return keys;
}

function sectionByKey(form: IntakeForm, key: string): IntakeFormSection | undefined {
  const raw = form.sections as unknown as Record<string, IntakeFormSection> | undefined;
  if (raw?.[key]) {
    return raw[key];
  }
  const listed = raw
    ? Object.entries(raw).find(([mapKey, section]) => section && (section.key || mapKey) === key)
    : undefined;
  if (listed) {
    return listed[1];
  }
  return (form.custom_sections ?? []).find(section => (section.key ?? section.title) === key);
}

function hiddenCopy(form: IntakeForm, key: string): CustomField | undefined {
  for (const section of sectionsOf(form)) {
    const found = (section.fields ?? []).find(field => field.field === key && field.hide_from_intake);
    if (found) {
      return found;
    }
  }
  return undefined;
}

function stripHiddenCopies(form: IntakeForm, key: string, keep: IntakeFormSection): void {
  sectionsOf(form).forEach(section => {
    if (section === keep || !section.fields) {
      return;
    }
    section.fields = section.fields.filter(field => !(field.field === key && field.hide_from_intake));
  });
}

/**
 * Copy a custom field the firm just placed into the section the sidebar reads.
 *
 * Creating a field writes the org catalog, and the server also files a hidden copy under Internal
 * Fields. Placing it on the v2 canvas writes a `field_key` onto a row and nothing else — `applyPages`
 * does not touch `fields`, and publish promotes that draft verbatim. The eviction sidebar and the
 * inbox list `section.fields`, so a value stored under `custom_fields` still has nowhere to render
 * until the placed section carries the definition.
 *
 * A field already listed on a section is left where it is. That is every migrated field.
 * Headings, dividers and rich text are not fields and are never copied.
 */
export function includePlacedCustomFields(
  form: IntakeForm,
  pages: LayoutPageView[],
  orgCustomFields: CustomField[] = []
): IntakeForm {
  const catalog = new Map(orgCustomFields.filter(field => field.field).map(field => [field.field, field]));

  // Where each field is placed *now*: the first row that carries it, and the section that row is in.
  const placedIn = new Map<string, IntakeFormSection>();
  const onCanvas = new Set<IntakeFormSection>();
  pages.forEach(page =>
    page.sections.forEach(view => {
      const target = sectionByKey(form, view.key);
      if (!target) {
        return;
      }
      onCanvas.add(target);
      view.rows.forEach(row =>
        row.items.forEach(item => {
          if (item.kind === 'FIELD' && item.field_key && !placedIn.has(item.field_key)) {
            placedIn.set(item.field_key, target);
          }
        })
      );
    })
  );

  placedIn.forEach((target, key) => {
    // Built-ins are listed from `built_in_fields`, never copied.
    if (sectionsOf(form).some(section => (section.built_in_fields ?? []).some(field => field.field === key))) {
      return;
    }
    // The visible copy wherever it sits now. A hidden one is the Internal Fields filing, not a listing.
    const holder = sectionsOf(form).find(section =>
      (section.fields ?? []).some(field => field.field === key && !field.hide_from_intake)
    );
    if (holder === target) {
      return;
    }
    const moved = holder?.fields?.find(field => field.field === key);
    const source = moved ?? catalog.get(key) ?? hiddenCopy(form, key);
    if (!source) {
      return;
    }
    if (holder && moved) {
      // The row moved to another section: the sidebar and inbox list `section.fields`, so the copy follows it.
      holder.fields = holder.fields.filter(field => field !== moved);
    }
    target.fields = target.fields ?? [];
    const existing = target.fields.find(field => field.field === key);
    if (existing) {
      existing.hide_from_intake = false;
    } else {
      target.fields.push({ ...source, hide_from_intake: false, order: target.fields.length });
    }
    stripHiddenCopies(form, key, target);
  });

  // A custom field whose row was taken off the canvas is no longer on the form: return its copy to the
  // hidden state the server files a new field in, rather than leaving it listed on a section it is not on.
  // Only sections the canvas draws, and only custom fields — a built-in or a never-placeable key is not
  // something removing a row can un-list.
  onCanvas.forEach(section =>
    (section.fields ?? []).forEach(field => {
      if (
        !field.hide_from_intake &&
        !field.built_in_field &&
        isIntakePlaceable(field.field) &&
        !COMPOSITE_PART_KEYS.has(field.field) &&
        !placedIn.has(field.field) &&
        section.layout_version === LAYOUT_VERSION_ROWS
      ) {
        field.hide_from_intake = true;
      }
    })
  );

  return form;
}

/**
 * The registry, as the picker and the inspector need it. Built from the form's own fields plus the org's
 * custom fields, so a field that exists but is not placed still shows up in the picker.
 */
export function toDefinitions(form: IntakeForm, orgCustomFields: CustomField[] = []): FieldDefinitionView[] {
  const seen = new Map<string, FieldDefinitionView>();

  /*
   * The declared sections first.
   *
   * Their questions live in wizard markup rather than in the registry, so without this the preview had
   * no definition to render from and fell through to a bare text box for every one of them — a preview
   * that does not look like the form it is previewing.
   *
   * First, so a registry field of the same key cannot overwrite what the wizard actually draws; that is
   * the same mistake that had Business Information asking for "Plaintiff Name".
   */
  const formOptions = (form.options ?? {}) as unknown as Record<string, { options?: string[] }>;
  // Radio vs checkboxes, the firm's labels, and the default — one derivation, shared with the legacy form.
  const evictionReason = evictionReasonFieldFromOptions(form.options);
  PUBLIC_FORM_SECTIONS.flatMap(section => section.rows.flat()).forEach(cell => {
    const reason = cell.key === 'eviction_reason' ? evictionReason : undefined;
    const narrowed = formOptions[cell.option ?? cell.key]?.options;
    const type = reason?.type ?? cell.type ?? CustomFieldType.TEXT;
    // The firm's own choice list where it set one, so the preview draws the control it will render and
    // the migration carries the configuration rather than silently reverting to the defaults.
    const options = reason?.options?.length ? reason.options : narrowed?.length ? narrowed : cell.options;
    seen.set(cell.key, {
      key: cell.key,
      label: cell.label,
      type,
      options,
      size_hint: sizeHintForColumns(undefined),
      origin: 'BUILT_IN',
      // Carried through so the canvas and the preview know to draw the block rather than one control,
      // and so the picker places it as a unit.
      composite: cell.composite,
      /*
       * Both halves of the choice list, where the wizard has one.
       *
       * `options` above is the *effective* list; these say what the product's own is and where a
       * narrowed one is stored, which is what lets the placement panel edit it and offer a reset that
       * keeps following the product rather than freezing a copy of today's list.
       */
      standard_options: cell.options,
      option_key: cell.options ? (cell.option ?? cell.key) : undefined,
      // How the amount is drawn, checked, summed and started — what v1's markup and form builder knew.
      monetary: cell.monetary,
      include_in_total: cell.include_in_total,
      subtract_from_total: cell.subtract_from_total,
      positive: cell.positive,
      initial: cell.initial,
      section_total: cell.section_total,
      smart: type === CustomFieldType.CALCULATED,
      // Labels, choice layout and the default live on the field. Without it ee-field would draw the
      // values and a checkbox firm would be a dropdown.
      // A declared choice layout is the field's default too, so a placement that picks the other one is
      // an override and picking it back clears it — the same as for a registry field.
      ...(reason
        ? { field: reason }
        : cell.choiceLayout
          ? { field: { field: cell.key, label: cell.label, type, options, choice_layout: cell.choiceLayout } }
          : {})
    });
  });
  // A brand-new custom field is filed under Internal Fields with `hide_from_intake`, which keeps it
  // out of the picker. Once it is also sitting on a row, that hidden copy is the only definition the
  // public form has — the catalog endpoint is attorney-only — and skipping it is how the wizard draws
  // a plain text box and files the answer outside `custom_fields`.
  const placed = placedFieldKeys(form);

  const add = (field: CustomField, origin: 'BUILT_IN' | 'CUSTOM') => {
    if (!field.field || seen.has(field.field)) return;
    // Never offered, so the picker cannot place one — see NEVER_ON_INTAKE.
    if (!isIntakePlaceable(field.field)) return;
    if (field.hide_from_intake && !placed.has(field.field)) return;
    /*
     * A part of a composite is not placeable on its own. The org record holds `property_city` and the
     * rest as ordinary custom fields — that is how v1 stored them — so without this the picker would
     * offer six controls the address autocomplete overwrites on the client's first keystroke.
     */
    if (COMPOSITE_PART_KEYS.has(field.field)) return;
    seen.set(field.field, {
      key: field.field,
      label: field.label ?? field.field,
      type: field.type,
      options: field.options,
      hint: field.hint,
      size_hint: sizeHintForColumns(field.columns),
      origin: field.built_in_field ? 'BUILT_IN' : origin,
      smart: field.type === CustomFieldType.CALCULATED || field.type === CustomFieldType.DATE_OFFSET,
      monetary: field.monetary ?? undefined,
      // The source field travels with the view so ee-field draws everything the firm configured.
      field,
      include_in_total: field.include_in_total,
      subtract_from_total: field.subtract_from_total
    });
  };

  // Custom sections too: `includePlacedCustomFields` files the copy there when the row sits in one, and
  // a definition missing from this list is how the answer ends up outside `custom_fields`.
  sectionsOf(form).forEach(section => {
    (section.built_in_fields ?? []).forEach(field => add(field, 'BUILT_IN'));
    (section.fields ?? []).forEach(field => add(field, 'CUSTOM'));
  });
  orgCustomFields.forEach(field => add(field, 'CUSTOM'));

  // Typed or computed, by what the form places beside it — see `withComputedTotals`.
  return [...withComputedTotals(seen, toPages(form)).values()];
}

/**
 * A section total is typed or computed by what sits beside it.
 *
 * Alone on its section it is the question — v1's one Total Due. Once any amount that counts toward it
 * is placed on the same section (a declared one, or a firm field flagged `include_in_total`), it is the
 * read-only sum, the way v1's "Total Amount" line was. Decided from the *pages being asked*: a host
 * that has removed the firm-only amounts for a client (`withoutAttorneyOnly`) applies it again, so the
 * client is never shown a computed total with nothing on their page to compute it from.
 */
export function withComputedTotals(
  definitions: Map<string, FieldDefinitionView>,
  pages: LayoutPageView[]
): Map<string, FieldDefinitionView> {
  const out = new Map(definitions);
  pages
    .flatMap(page => page.sections)
    .forEach(section => {
      const placed = section.rows
        .flatMap(row => row.items)
        .map(item => out.get(item.field_key ?? ''))
        .filter((definition): definition is FieldDefinitionView => !!definition);
      const totals = placed.filter(definition => definition.section_total);
      if (!totals.length) return;
      const computed = placed.some(d => !d.section_total && (d.include_in_total || d.subtract_from_total));
      totals.forEach(definition =>
        out.set(definition.key, {
          ...definition,
          // A total is always an amount; what changes is whether the client or the form supplies it.
          type: computed ? CustomFieldType.CALCULATED : CustomFieldType.DECIMAL,
          smart: computed
        })
      );
    });
  return out;
}

/** Every row on the form, flattened — used for counts and for the picker's already-placed set. */
export function allRows(pages: LayoutPageView[]): LayoutRow[] {
  return pages.flatMap(page => page.sections.flatMap(section => section.rows));
}
